import { useState, type FormEvent } from "react";

const FORM_ENDPOINT = (import.meta.env.VITE_ENQUIRY_ENDPOINT as string | undefined) ?? "";
const RECIPIENT = "silvan@fivequantum.com";

type Status = "idle" | "submitting" | "success" | "error";

export function EnquiryForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [errorMsg, setErrorMsg] = useState<string>("");

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (status === "submitting") return;

    const form = event.currentTarget;
    const data = new FormData(form);
    // Honeypot — silently drop bot submissions
    if ((data.get("company_website") as string)?.trim()) {
      setStatus("success");
      form.reset();
      return;
    }

    // If no endpoint is configured, fall back to a pre-filled mailto so the
    // form still works without backend infrastructure.
    if (!FORM_ENDPOINT) {
      const subject = encodeURIComponent(
        `[FIVE QUANTUM] Enquiry · ${data.get("name") ?? ""}`,
      );
      const body = encodeURIComponent(
        [
          `Name: ${data.get("name") ?? ""}`,
          `Entity: ${data.get("entity") ?? ""}`,
          `Email: ${data.get("email") ?? ""}`,
          `Capacity: ${data.get("capacity") ?? ""}`,
          `Asset Class of Interest: ${data.get("asset_class") ?? ""}`,
          `Indicative Volume: ${data.get("volume") ?? ""}`,
          "",
          "Message:",
          `${data.get("message") ?? ""}`,
        ].join("\n"),
      );
      window.location.href = `mailto:${RECIPIENT}?subject=${subject}&body=${body}`;
      setStatus("success");
      return;
    }

    setStatus("submitting");
    setErrorMsg("");
    try {
      const payload: Record<string, string> = { _replyto: String(data.get("email") ?? "") };
      data.forEach((v, k) => {
        payload[k] = String(v);
      });
      payload._subject = `[FIVE QUANTUM] Enquiry · ${payload.name ?? ""}`;
      payload._to = RECIPIENT;

      const res = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        throw new Error(`Submission failed (${res.status})`);
      }
      setStatus("success");
      form.reset();
    } catch (err) {
      setStatus("error");
      setErrorMsg(err instanceof Error ? err.message : "Submission failed");
    }
  }

  if (status === "success") {
    return (
      <div className="form-success" role="status" aria-live="polite">
        <div className="form-success__seal">·</div>
        <h3 className="form-success__title">Directive received.</h3>
        <p className="form-success__body">
          A principal will respond from {RECIPIENT} under strict confidentiality.
          Expect a formal acknowledgement within two business days.
        </p>
      </div>
    );
  }

  return (
    <form className="enquiry-form" onSubmit={onSubmit} noValidate>
      <div className="form-row">
        <label className="field">
          <span className="field__label">Full Name</span>
          <input name="name" type="text" required autoComplete="name" />
        </label>
        <label className="field">
          <span className="field__label">Entity / Office</span>
          <input name="entity" type="text" required autoComplete="organization" />
        </label>
      </div>

      <div className="form-row">
        <label className="field">
          <span className="field__label">Confidential Email</span>
          <input name="email" type="email" required autoComplete="email" />
        </label>
        <label className="field">
          <span className="field__label">Capacity</span>
          <select name="capacity" required defaultValue="">
            <option value="" disabled>Select capacity…</option>
            <option value="Principal">Principal</option>
            <option value="Family Office">Family Office</option>
            <option value="Foundation">Foundation</option>
            <option value="Sovereign / Institutional">Sovereign / Institutional</option>
            <option value="Licensed Intermediary">Licensed Intermediary</option>
          </select>
        </label>
      </div>

      <div className="form-row">
        <label className="field">
          <span className="field__label">Asset Class of Interest</span>
          <select name="asset_class" required defaultValue="">
            <option value="" disabled>Select…</option>
            <option value="Gold">Gold</option>
            <option value="Institutional Digital Assets">Institutional Digital Assets</option>
            <option value="Global Banking Navigation">Global Banking Navigation</option>
            <option value="Cognitive / AI Infrastructure">Cognitive / AI Infrastructure</option>
            <option value="Multiple / Bespoke Mandate">Multiple / Bespoke Mandate</option>
          </select>
        </label>
        <label className="field">
          <span className="field__label">Indicative Volume (USD)</span>
          <input name="volume" type="text" placeholder="e.g. $50M+ / Confidential" />
        </label>
      </div>

      <label className="field field--block">
        <span className="field__label">Directive · Brief Description</span>
        <textarea
          name="message"
          rows={5}
          placeholder="Outline the structural objective, jurisdictional considerations, and any timing parameters."
          required
        />
      </label>

      {/* Honeypot */}
      <label className="hp" aria-hidden="true" tabIndex={-1}>
        Company Website
        <input name="company_website" type="text" tabIndex={-1} autoComplete="off" />
      </label>

      <div className="form-actions">
        <button className="form-submit" type="submit" disabled={status === "submitting"}>
          {status === "submitting" ? "Transmitting…" : "Submit Directive"}
        </button>
        <span className="form-note">
          All submissions are encrypted in transit and routed under strict confidentiality.
        </span>
      </div>

      {status === "error" && (
        <div className="form-error" role="alert">
          {errorMsg || "Submission failed. Please retry or write to us directly."}
        </div>
      )}
    </form>
  );
}
